import { FieldValues, useController } from "react-hook-form";
import styled from "styled-components";
import { ChangeEvent, useEffect, useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

import { DefaultProps, CurrencyFieldProps } from "../FormField";
import { Input } from "@/components/Forms";
import Text from "@/components/Text";
import { TestingCurrencies } from "@/utils/enums";

const BudgetInputsWrapper = styled.div`
  display: flex;
`;

const DisabledInput = styled(Input)`
  border-bottom-left-radius: 0;
  border-top-left-radius: 0;
  border-left-color: transparent;
  padding-left: 30px;
  color: ${({ theme }) => theme.colors.gray};
`;

const StyledInput = styled(Input)`
  border-bottom-right-radius: 0;
  border-top-right-radius: 0;
  padding-left: 34px;
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const InputHolder = styled.div`
  position: relative;
  width: 100%;
`;

const Currency = styled(Text)`
  position: absolute;
  left: 16px;
  top: 12px;
`;

const renderCurrencySymbol = (currency: TestingCurrencies) => {
  switch (currency) {
    case TestingCurrencies.KRW:
      return "₩";
    case TestingCurrencies.EUR:
      return "€";
    case TestingCurrencies.INR:
      return "₹";
    case TestingCurrencies.IDR:
      return "Rp";
    default:
      return "$";
  }
};

type Props<T extends FieldValues> = Omit<
  DefaultProps<T> & CurrencyFieldProps,
  "type"
>;

const CurrencyField = <T extends FieldValues>({
  name,
  control,
  inputType,
  suggestionsEndpoint,
  currency = TestingCurrencies.KRW,
  ...rest
}: Props<T>) => {
  const {
    field: { onChange, ...restOfField },
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const [valueInUsd, setValueInUsd] = useState<null | string>(null);

  const { data: currencyRate } = useQuery(
    [`${currency}_to_usd_currency_rate`],
    () =>
      axios
        .get(
          `https://api.exchangerate.host/convert?from=${currency}&to=USD&amount=100`
        )
        .then((response) => response.data?.info?.rate),
    {
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    if (!currencyRate || !restOfField.value) {
      return setValueInUsd(null);
    }

    setValueInUsd((restOfField.value * currencyRate).toFixed(2));
  }, [currencyRate, restOfField.value]);

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.validity.valid ? e.target.value : restOfField.value);
  };

  return (
    <BudgetInputsWrapper>
      <InputHolder>
        <StyledInput
          $hasError={!!error}
          pattern="[0-9]*"
          type="text"
          {...rest}
          {...restOfField}
          onChange={onInputChange}
        />
        <Currency $type="captionSemibold">
          {renderCurrencySymbol(currency)}
        </Currency>
      </InputHolder>
      <InputHolder>
        <DisabledInput value={valueInUsd ?? "-"} disabled readOnly />
        <Currency $color="gray" $type="captionSemibold">
          $
        </Currency>
      </InputHolder>
    </BudgetInputsWrapper>
  );
};

export default CurrencyField;
